import type { Address } from '.'
import { Selection } from '../enums'
import { isIn } from '../Utils/comparisonUtils'

export type PersonDto = {
  id?: number
  type: Selection
  address: BaseAddressDto
} & NaturalPersonDto & LegalPersonDto

export type PersonAdditionalData = {
  titleBefore: string | null
  titleAfter: string | null
  phone: string | null
  mobilePhonePrefix: string | null
  mobilePhone: string | null
  email: string | null
  agreement: boolean
  agreementOther?: boolean
  agent?: Agent
} & PersonDto

export type NaturalPersonDto = {
  birthNumber?: string | null
  name?: string | null
  surname?: string | null
  isForeigner: boolean
  birthday?: Date | null
  nationalityId?: number | null
}

export type LegalPersonDto = {
  idendificationNumber?: string | null
  companyName?: string | null
  isVatPayer?: boolean | null
}

export type BaseAddressDto = {
  postCode: string
  country: string
  city: string
  street?: string
  streetNumber?: string
  addressPoint?: number
  isEvidenceNumber?: boolean
}

export type Agent = {
  id: number | null
  titleBefore: string | null
  name: string | null
  surname: string | null
  required: boolean | null
}

export interface FindAddressResult {
  adresniMisto: number
  cisloDomovni?: string
  cisloOrientacni?: string
  typSo?: string
  psc: string
  ulice?: string
  obec: string
  castObce?: string
  adresa: string
}

export interface KadastralAreaInfo {
  id: number
  nazev: string
  obecKod: number
  obecNazev: string
  oddeleneRady: boolean
}

export const formatPersonFullName = (person?: PersonAdditionalData) => {
  if (!person) return ''

  const name = `${person?.titleBefore ?? ''} ${person?.name ?? ''} ${person?.surname ?? ''} ${person?.titleAfter ?? ''}`.trim()

  return (isIn(person.type, Selection.Person, Selection.SelfEmployed) && !!name
    ? name
    : person.companyName ?? '')
    .trim()
}

export const formatPersonAddress = (address?: BaseAddressDto | Address) => {
  if (!address) return ''

  const ph = []

  let ulice = address.street ?? ''
  let obec = address.city ?? ''
  const cityParts = (address.city ?? '').split(' - ')
  if (!ulice) {
    if (cityParts.length == 2) {
      ulice = cityParts[1]
      obec = cityParts[0]
    }
    if (cityParts.length == 1) {
      ulice = obec
    }
  }

  if ('cityWithCityPart' in address === true) {
    // Na interface (majetek) je přímo cityWithCityPart
    const cwcp = (address as Address).cityWithCityPart ?? ''
    if (cwcp.length > 0) {
      obec = cwcp
    }
  }

  const street = `${ulice} ${address.isEvidenceNumber ? 'č. ev. ' : ''}${address.streetNumber ?? ''}`
  if (street.trim()) ph.push(street)

  const city = `${address.postCode ?? ''} ${obec}`
  if (city.trim()) ph.push(city)

  return ph.join(', ')
}

export const formatPerson = (person: PersonAdditionalData) => {
  const ph = []

  const fullname = formatPersonFullName(person)
  if (fullname?.trim()) ph.push(fullname)

  const address = formatPersonAddress(person.address)
  if (address.trim()) ph.push(address)

  return ph.join(', ')
}

export const formatPersonRcIco = (person?: PersonAdditionalData) => {
  if (!person) return ''

  return ((person.type == Selection.Person
    ? person.birthNumber : person.idendificationNumber) ?? '')
    .trim()
}